import { EVENT_USER_LANGUAGE_CHANGED, EVENT_USER_PROFILE_CHANGED } from '@rio-cloud/rio-user-menu-component';
import { userProfileObtained, userSessionExpired, userSessionRenewed } from './login/loginSlice';
import { configureMockUserManager, configureUserManager, createUserManager } from './login/login';
import { store } from './setup/store';
import { accessToken } from './tokenHandling/accessToken';
import { trace } from './setup/trace';
import { attemptInitialSignIn } from './setup/oauth';
import { config } from '../config';
import { reportErrorToSentry } from './setup/sentry';
import { accessTokenStored, idTokenStored } from './tokenHandling/tokenHandlingSlice';
import { getLocale } from './lang/langSlice';
import { extractLanguage } from './lang/lang';
import { configureFetchDisplayMessages } from './lang/services';
import type { User } from 'oidc-client-ts';
import type { OAuthConfig, SessionRenewedResult } from './types';

export const main = async (renderApp: (user: User | null) => void) => {
    const fetchDisplayMessages = configureFetchDisplayMessages(store);

    // We want the `<html lang>` attribute to be synced with the
    // language currently displayed
    store.subscribe(() => {
        const lang = extractLanguage(getLocale(store.getState()));
        const html = document.querySelector('html');

        if (html && lang && html.getAttribute('lang') !== lang) {
            html.setAttribute('lang', lang);
        }
    });

    const oauthConfig: OAuthConfig = {
        onSessionExpired: () => {
            accessToken.discardAccessToken();
            store.dispatch(userSessionExpired());
        },
        onSessionRenewed: (result: SessionRenewedResult) => {
            trace('index.onTokenRenewed', result);

            accessToken.saveAccessToken(result.accessToken);
            store.dispatch(accessTokenStored(result.accessToken));
            store.dispatch(idTokenStored(result.idToken));
            store.dispatch(userProfileObtained(result.profile));

            store.dispatch(userSessionRenewed());

            // You will need to get the user language by yourself then
            // you may fetch the suitable messages. Depending
            // on when and from where you fetch the user settings you might
            // want to employ a loading spinner while the request is ongoing.
            fetchDisplayMessages(result.locale);
        },
    };

    // enables mocking of authentication in production
    const testAccessToken = sessionStorage?.getItem('test_access_token');
    // enables mocking of authentication in production
    const testIdToken = sessionStorage?.getItem('test_id_token');

    const isAllowedToMockAuth = import.meta.env.DEV;
    const userManager =
        (isAllowedToMockAuth && config.login.mockAuthorization) || testAccessToken
            ? configureMockUserManager(oauthConfig, {
                  accessToken: testAccessToken,
                  idToken: testIdToken,
                  store,
              })
            : configureUserManager(oauthConfig, createUserManager());

    document.addEventListener(EVENT_USER_LANGUAGE_CHANGED, userManager.signinSilent.bind(userManager) as EventListener);
    document.addEventListener(EVENT_USER_PROFILE_CHANGED, userManager.signinSilent.bind(userManager) as EventListener);

    try {
        userManager.clearStaleState().catch(error => reportErrorToSentry(error));
    } catch (error) {
        reportErrorToSentry(error);
    }

    try {
        const user = await attemptInitialSignIn(userManager);
        renderApp(user);
    } catch (error: unknown) {
        trace('could not start application', error);
    }
};
